import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Alert } from '@neo/primitives/alert'
import { ButtonWithLoader } from '@neo/primitives/button'
import { AlertCircle } from 'lucide-react'
import { useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useNavigate, useSearchParams } from 'router-v6'
import { RECAPTCHA_SITE_KEY } from 'v2/configs/constants'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { AuthLayout } from 'v2/shared/layouts/auth'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { nameSchema } from 'v2/shared/validation-schemas'
import type { RLoginResponse } from 'v2/types/api/auth'
import { AUTH_INPUT_CLASS, AUTH_PRIMARY_BUTTON_CLASS } from './components/auth-styles'
import { useActivateInvitationV2Mutation } from './queries'
import { validatePassword } from './utils'
import { useSessionManager } from './utils/session-utils'
import { redirectToTwoFa } from './utils/two-fa-utils'

interface JoinTeamFormData {
  firstName: string
  lastName: string
  password: string
}

const newUserFields: GenericFieldConfig<JoinTeamFormData> = [
  {
    name: 'firstName',
    componentType: 'input',
    zodValidation: nameSchema('First name', 30),
    label: 'First Name',
    inputProps: {
      placeholder: 'Enter your first name',
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'given-name',
      'data-testid': 'input-first-name',
    },
  },
  {
    name: 'lastName',
    componentType: 'input',
    zodValidation: nameSchema('Last name', 30),
    label: 'Last Name',
    inputProps: {
      placeholder: 'Enter your last name',
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'family-name',
      'data-testid': 'input-last-name',
    },
  },
  {
    name: 'password',
    componentType: 'inputPassword',
    zodValidation: (formState: JoinTeamFormData) => {
      return validatePassword(formState.firstName, formState.lastName)
    },
    label: 'Password',
    errorClassName: 'error-list-format',
    inputProps: {
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'new-password',
      placeholder: 'Create a password',
      'data-testid': 'input-password',
    },
  },
]

const newUserDefaultValues = {
  firstName: '',
  lastName: '',
  password: '',
}

export const InvitationsV2 = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const region = searchParams.get('region')
  const company = searchParams.get('company')
  const email = searchParams.get('email')
  const isValid = Boolean(token && region)
  const isExistingUser = searchParams.get('is_existing_user') === 'true'
  const companyName = company || 'your Organization'
  const recaptchaRef = useRef() as React.MutableRefObject<ReCAPTCHA>
  const {
    mutateAsync: activateInvitationV2,
    isPending: isActivatingInvitation,
    error,
  } = useActivateInvitationV2Mutation(recaptchaRef)
  const { completeLoginProcess } = useSessionManager()

  const handleActivationSuccess = (loginResponse: RLoginResponse) => {
    if (loginResponse.two_fa_enabled) {
      redirectToTwoFa({ loginResponse, navigate, source: 'invite' })
    } else {
      completeLoginProcess(loginResponse)
      window.location.href = '/home'
    }
  }

  const onSubmitNewUser = async (values: JoinTeamFormData) => {
    if (!token || !region) return

    try {
      const response = await activateInvitationV2({
        token,
        region,
        check_token: false,
        first_name: values.firstName,
        last_name: values.lastName,
        password: values.password,
      })
      handleActivationSuccess(response.data)
    } catch {
      // Error handled by mutation's onError
    }
  }

  const onSubmitExistingUser = async () => {
    if (!token || !region) return

    try {
      const response = await activateInvitationV2({
        token,
        region,
        check_token: false,
      })
      handleActivationSuccess(response.data)
    } catch {
      // Error handled by mutation's onError
    }
  }

  if (!isValid) {
    return (
      <AuthLayout
        pageTitle="Invalid token"
        title="Invalid invitation"
        description="This invitation link is invalid or has expired."
      >
        <div className="flex items-center gap-2 p-4 border border-destructive rounded-md text-destructive">
          <AlertCircle className="h-4 w-4" />
          <p className="text-sm">Invalid invitation</p>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      pageTitle={`Join ${companyName}`}
      title={`Join ${companyName}`}
      description={
        isExistingUser ? (
          <p className="text-p-400 text-secondary">
            You're accepting an invitation sent to <span className="text-s-600">{email}</span>.
          </p>
        ) : (
          'Complete your profile to join your team.'
        )
      }
    >
      {error && <Alert variant="destructive" content={getErrorText(error)} />}
      {isExistingUser ? (
        <ButtonWithLoader
          className="w-full"
          size="lg"
          loading={isActivatingInvitation}
          onClick={onSubmitExistingUser}
          data-testid="button-accept-invite"
        >
          Accept & Join Team
        </ButtonWithLoader>
      ) : (
        <>
          <GenericForm
            id="join-team-form"
            defaultValues={newUserDefaultValues}
            fields={newUserFields}
            onSubmit={onSubmitNewUser}
            className="grid gap-4"
            submitText="Join Team"
            includeSubmitButton={false}
          />
          <FormSubmitButton
            formId="join-team-form"
            typography="action"
            className={AUTH_PRIMARY_BUTTON_CLASS}
            size="xl"
            loading={isActivatingInvitation}
            data-testid="button-join-team"
          >
            Join Team
          </FormSubmitButton>
        </>
      )}
      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITE_KEY} />
    </AuthLayout>
  )
}
